import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';
import AuthService from '../../services/authService';
import { getImageURL } from '../../utils/api';
import { batchStatusBadgeClass, formatStatusLabel } from '../../utils/batchStudentStatusHelpers';

const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderColor: state.isFocused ? '#059669' : '#d1d5db',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(5,150,105,0.2)' : 'none',
    '&:hover': { borderColor: '#059669' },
    borderRadius: '0.5rem', minHeight: '42px', fontSize: '0.875rem',
  }),
  placeholder: (base) => ({ ...base, color: '#9ca3af', fontSize: '0.875rem' }),
  menu: (base) => ({ ...base, borderRadius: '0.5rem', boxShadow: '0 10px 25px rgba(0,0,0,0.1)', border: '1px solid #e5e7eb', zIndex: 20 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? '#d1fae5' : state.isFocused ? '#ecfdf5' : 'white',
    color: state.isSelected ? '#065f46' : '#1f2937', cursor: 'pointer',
  }),
  multiValue: (base) => ({ ...base, backgroundColor: '#d1fae5' }),
  multiValueLabel: (base) => ({ ...base, color: '#065f46' }),
};

const BATCH_STATUS_TABS = [
  { value: 'ongoing', label: 'Ongoing' },
  { value: 'not started', label: 'Not Started' },
  { value: 'delivery completed', label: 'Delivery Completed' },
  { value: 'batch completed', label: 'Batch Completed' },
  { value: 'all', label: 'All' },
];

// Two independent dimensions (student side, parent side), each All/Shared/Not
// Shared, combined with AND -- covers every combination (e.g. "both shared",
// "neither shared", "student shared regardless of parent") without needing a
// separate mutually-exclusive tab per combination.
const SHARE_FILTER_TABS = [
  { value: 'all', label: 'All' },
  { value: 'shared', label: 'Shared' },
  { value: 'not-shared', label: 'Not Shared' },
];

function matchesShareFilter(isShared, filter) {
  if (filter === 'shared') return isShared;
  if (filter === 'not-shared') return !isShared;
  return true; // 'all'
}

function formatScheduleType(scheduleType) {
  if (!scheduleType) return '';
  return scheduleType.charAt(0).toUpperCase() + scheduleType.slice(1);
}

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

// Green check-circle / red x-circle -- the user explicitly wants icons here,
// not the badge-pill convention other reports (e.g. University ID Status) use.
function StatusIcon({ shared, title }) {
  return shared ? (
    <svg className="h-5 w-5 text-emerald-600" fill="currentColor" viewBox="0 0 20 20" aria-label={title} title={title}>
      <path fillRule="evenodd" clipRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" />
    </svg>
  ) : (
    <svg className="h-5 w-5 text-red-600" fill="currentColor" viewBox="0 0 20 20" aria-label={title} title={title}>
      <path fillRule="evenodd" clipRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" />
    </svg>
  );
}

export default function StudentPortalAccessReport() {
  const navigate = useNavigate();

  const [accessibleCourseIds, setAccessibleCourseIds] = useState([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [accessLoaded, setAccessLoaded] = useState(false);

  const [courseOptions, setCourseOptions] = useState([]);
  const [courseOptionsLoading, setCourseOptionsLoading] = useState(true);
  const [selectedCourses, setSelectedCourses] = useState([]); // [] = all accessible courses
  const [selectedStatus, setSelectedStatus] = useState('ongoing');
  const [studentShareFilter, setStudentShareFilter] = useState('all');
  const [parentShareFilter, setParentShareFilter] = useState('all');
  const [search, setSearch] = useState('');

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // "Management" staff get the same unrestricted report access as Super Admin
  // (see RequireReportAccess in App.js) — without this, they'd otherwise be
  // narrowed down to their own (typically empty) coordinate_courses list.
  const isManagement = String(AuthService.getUser()?.superAdmin?.staff?.staffType || '').trim().toLowerCase() === 'management';

  const accessibleParam = useCallback(() => {
    if (isSuperAdmin || isManagement) return 'all';
    return (Array.isArray(accessibleCourseIds) ? accessibleCourseIds : [])
      .map((id) => Number(id))
      .filter((num) => Number.isFinite(num) && num > 0);
  }, [isSuperAdmin, isManagement, accessibleCourseIds]);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const accessRes = await axiosInstance.get('/admin/me/coordinate-courses').catch(() => ({ data: {} }));
        if (!isMounted) return;
        const data = accessRes.data || {};
        setAccessibleCourseIds(Array.isArray(data.accessibleCourseIds) ? data.accessibleCourseIds : []);
        setIsSuperAdmin(!!data.isSuperAdmin);
      } catch (e) {
        console.error('Failed to load coordinate courses', e);
      } finally {
        if (isMounted) setAccessLoaded(true);
      }
    })();
    return () => { isMounted = false; };
  }, []);

  // Derive the course filter's option list across every batch status (not
  // just the currently selected tab) so switching tabs never hides a course
  // from the filter that still has batches under a different status.
  useEffect(() => {
    if (!accessLoaded) return;
    let isMounted = true;
    (async () => {
      setCourseOptionsLoading(true);
      try {
        const resp = await axiosInstance.get('/admin/dashboard/student-portal-access', {
          params: { accessibleCourses: accessibleParam(), courseIds: 'all', batchStatus: 'all' },
        });
        if (!isMounted) return;
        const list = Array.isArray(resp?.data?.courses) ? resp.data.courses : [];
        const options = list
          .filter((c) => c.courseId)
          .map((c) => {
            const baseName = c.courseShortName || c.courseName || `Course #${c.courseId}`;
            const scheduleLabel = formatScheduleType(c.scheduleType);
            return { value: c.courseId, label: scheduleLabel ? `${baseName} (${scheduleLabel})` : baseName };
          })
          .sort((a, b) => a.label.localeCompare(b.label));
        setCourseOptions(options);
      } catch (e) {
        console.error('Failed to load course options', e);
      } finally {
        if (isMounted) setCourseOptionsLoading(false);
      }
    })();
    return () => { isMounted = false; };
  }, [accessLoaded, accessibleParam]);

  const loadReport = useCallback(async () => {
    if (!accessLoaded) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await axiosInstance.get('/admin/dashboard/student-portal-access', {
        params: {
          accessibleCourses: accessibleParam(),
          courseIds: selectedCourses.length > 0 ? selectedCourses.map((o) => o.value).join(',') : 'all',
          batchStatus: selectedStatus,
        },
      });
      const data = resp?.data || {};
      setCourses(data.courses || []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load student portal access report');
    } finally {
      setLoading(false);
    }
  }, [accessLoaded, accessibleParam, selectedCourses, selectedStatus]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const searchFilteredCourses = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return courses;
    return courses
      .map((course) => ({
        ...course,
        batches: course.batches
          .map((batch) => ({
            ...batch,
            students: batch.students.filter((s) => {
              const combined = [s.studentName, s.studentId, s.registrationNumber, s.username].filter(Boolean).join(' ').toLowerCase();
              return combined.includes(term)
                || (batch.batchInternalName || '').toLowerCase().includes(term)
                || (batch.batchName || '').toLowerCase().includes(term)
                || (course.courseName || '').toLowerCase().includes(term);
            }),
          }))
          .filter((batch) => batch.students.length > 0),
      }))
      .filter((course) => course.batches.length > 0);
  }, [courses, search]);

  const filteredCourses = useMemo(() => {
    if (studentShareFilter === 'all' && parentShareFilter === 'all') return searchFilteredCourses;
    return searchFilteredCourses
      .map((course) => ({
        ...course,
        batches: course.batches
          .map((batch) => ({
            ...batch,
            students: batch.students.filter((s) =>
              matchesShareFilter(s.portalShared, studentShareFilter)
              && matchesShareFilter(s.parentPortalShared, parentShareFilter)
            ),
          }))
          .filter((batch) => batch.students.length > 0),
      }))
      .filter((course) => course.batches.length > 0);
  }, [searchFilteredCourses, studentShareFilter, parentShareFilter]);

  const visibleCounts = useMemo(() => {
    let batches = 0;
    let students = 0;
    let portalShared = 0;
    let parentPortalShared = 0;
    filteredCourses.forEach((course) => {
      batches += course.batches.length;
      course.batches.forEach((batch) => {
        students += batch.students.length;
        batch.students.forEach((s) => {
          if (s.portalShared) portalShared += 1;
          if (s.parentPortalShared) parentPortalShared += 1;
        });
      });
    });
    return { batches, students, portalShared, parentPortalShared };
  }, [filteredCourses]);

  return (
    <DashboardLayout title="Student Portal Access Status">
      <div className="mx-auto max-w-7xl">
        <div className="mb-4 flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate('/admin/report/monitor')}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Monitor Report
          </button>
        </div>

        <div className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50 p-6">
          <h2 className="flex items-center gap-2 text-base font-semibold text-emerald-800">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            Student Portal Access Status
          </h2>
          <p className="mt-1 text-sm text-emerald-900">
            Batches grouped by course, showing whether each student's login (username/password) and their
            parent's portal login have been set up and shared.
          </p>

          <div className="mt-4 flex flex-wrap gap-2">
            {BATCH_STATUS_TABS.map((tab) => (
              <button
                key={tab.value}
                type="button"
                onClick={() => setSelectedStatus(tab.value)}
                className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${
                  selectedStatus === tab.value
                    ? 'bg-emerald-600 text-white shadow-sm'
                    : 'bg-white text-gray-600 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="mt-4 flex flex-wrap gap-6">
            <div>
              <label className="mb-1.5 block text-xs font-medium text-gray-700">Student Portal Status</label>
              <div className="flex flex-wrap gap-2">
                {SHARE_FILTER_TABS.map((tab) => (
                  <button
                    key={tab.value}
                    type="button"
                    onClick={() => setStudentShareFilter(tab.value)}
                    className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${
                      studentShareFilter === tab.value
                        ? 'bg-emerald-600 text-white shadow-sm'
                        : 'bg-white text-gray-600 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium text-gray-700">Parent Portal Status</label>
              <div className="flex flex-wrap gap-2">
                {SHARE_FILTER_TABS.map((tab) => (
                  <button
                    key={tab.value}
                    type="button"
                    onClick={() => setParentShareFilter(tab.value)}
                    className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${
                      parentShareFilter === tab.value
                        ? 'bg-emerald-600 text-white shadow-sm'
                        : 'bg-white text-gray-600 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-end gap-4">
            <div className="min-w-[240px] flex-1">
              <label className="mb-1 block text-xs font-medium text-gray-700">Courses</label>
              <Select
                isMulti
                isLoading={courseOptionsLoading}
                options={courseOptions}
                value={selectedCourses}
                onChange={(opts) => setSelectedCourses(opts || [])}
                placeholder="All accessible courses (default)…"
                styles={selectStyles}
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
              />
            </div>
            <div className="flex flex-col">
              <label className="mb-1 text-xs font-medium text-gray-700">Search</label>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Student / Username / Batch / Course"
                className="w-64 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
              />
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">{error}</div>
        )}

        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-gray-700">
            {visibleCounts.batches} batch{visibleCounts.batches === 1 ? '' : 'es'} &middot; {visibleCounts.students} student{visibleCounts.students === 1 ? '' : 's'}
          </h3>
          <div className="flex items-center gap-3 text-xs font-semibold">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-3 py-1 text-emerald-700">
              <StatusIcon shared /> Student Portal: {visibleCounts.portalShared}/{visibleCounts.students}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-3 py-1 text-emerald-700">
              <StatusIcon shared /> Parent Portal: {visibleCounts.parentPortalShared}/{visibleCounts.students}
            </span>
          </div>
        </div>

        {loading ? (
          <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-gray-600">
            Loading student portal access status…
          </div>
        ) : filteredCourses.length === 0 ? (
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-8 text-center text-gray-600">
            No batches match the current filters.
          </div>
        ) : (
          <div className="space-y-10">
            {filteredCourses.map((course) => (
              <div key={course.courseId ?? course.courseName}>
                <h3 className="mb-3 text-lg font-bold text-gray-900">
                  {course.courseShortName || course.courseName || 'Unknown Course'}
                  {formatScheduleType(course.scheduleType) && ` (${formatScheduleType(course.scheduleType)})`}
                </h3>

                <div className="space-y-5">
                  {course.batches.map((batch) => (
                    <div key={batch.batchId} className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                      <div className="flex items-center justify-between gap-3 border-b border-gray-100 bg-gray-50 px-5 py-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="truncate text-sm font-semibold text-gray-800">
                              {batch.batchInternalName || batch.whatsappGroupName || batch.batchName || 'Batch'}
                            </p>
                            <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${batchStatusBadgeClass(batch.status)}`}>
                              {formatStatusLabel(batch.status)}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500">
                            {formatDate(batch.batchStartingDate)} – {formatDate(batch.batchEndingDate)}
                          </p>
                        </div>
                        <span className="shrink-0 rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold text-emerald-700">
                          {batch.students.length} student{batch.students.length === 1 ? '' : 's'}
                        </span>
                      </div>

                      <div className="overflow-x-auto">
                        <table className="w-full min-w-[720px] text-left">
                          <thead>
                            <tr className="border-b border-gray-100 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                              <th className="px-5 py-2.5">Student</th>
                              <th className="px-5 py-2.5">Username</th>
                              <th className="px-5 py-2.5 text-center">Student Portal Shared</th>
                              <th className="px-5 py-2.5 text-center">Parent Portal Shared</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {batch.students.map((s) => (
                              <tr key={`${s.studentId}-${s.registrationNumber}-${batch.batchId}`} className="hover:bg-gray-50">
                                <td className="px-5 py-3">
                                  <div className="flex items-center gap-3">
                                    <img
                                      src={getImageURL(s.imagePath)}
                                      alt={s.studentName}
                                      className="h-9 w-9 rounded-full object-cover ring-1 ring-gray-200"
                                    />
                                    <div className="min-w-0">
                                      <p className="max-w-[180px] truncate text-sm font-medium text-gray-800">{s.studentName || '—'}</p>
                                      <p className="text-[10px] text-gray-400">{s.registrationNumber || s.studentId || '—'}</p>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-5 py-3 text-xs text-gray-600">{s.username || '—'}</td>
                                <td className="px-5 py-3">
                                  <div className="flex justify-center">
                                    <StatusIcon shared={s.portalShared} title={s.portalShared ? 'Shared' : 'Not shared'} />
                                  </div>
                                </td>
                                <td className="px-5 py-3">
                                  <div className="flex justify-center">
                                    <StatusIcon shared={s.parentPortalShared} title={s.parentPortalShared ? 'Shared' : 'Not shared'} />
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
